import React, { useState, useRef, useEffect } from 'react';
import { MarkdownText } from './MarkdownText';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Bot, User, Sparkles, HelpCircle, FileText,
  BarChart3, Loader2, Lightbulb, Scale, AlertTriangle, CheckCircle,
  ArrowRight, Building2, Users, Home, Briefcase, Heart, Car, GraduationCap,
  Banknote, Shield, Gavel, Copy, RefreshCw, BookOpen, Zap, Award, Star,
  Clock, Search, ChevronDown, ChevronRight, MapPin, TrendingUp, Leaf,
  Receipt, FileCheck, Info, ExternalLink, PenTool
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { toast } from 'sonner';
import { JUDICIAL_SYSTEM, COMPETENCE_MATCHER, RECURSOS_PROCESALES, PLAZOS_JUDICIALES } from '@/data/judicialSystem';
import { FidemChat } from './fidem/FidemChat';

// ================ CONSTANTS & DATA ================

// Assistant Modes
const ASSISTANT_MODES = {
  copilot: { id: 'copilot', name: 'Fidem', icon: Sparkles, color: 'from-emerald-700 to-cyan-700', description: 'Tu copiloto legal' },
  general: {
    id: 'general',
    name: 'Asesor Legal',
    icon: Gavel,
    color: 'from-blue-600 to-cyan-500',
    description: 'Orientación legal general'
  },
  strategy: {
    id: 'strategy',
    name: 'Estrategia Legal',
    icon: TrendingUp,
    color: 'from-emerald-600 to-teal-500',
    description: 'Estrategia con jurisprudencia'
  },
  drafting: {
    id: 'drafting',
    name: 'Redacción Legal',
    icon: Sparkles,
    color: 'from-purple-600 to-pink-500',
    description: 'Cláusulas y excepciones'
  },
  judicial: {
    id: 'judicial',
    name: 'Sistema Judicial',
    icon: Scale,
    color: 'from-amber-600 to-orange-500',
    description: 'Tribunales y competencias'
  },
  history: {
    id: 'history',
    name: 'Historial',
    icon: Clock,
    color: 'from-slate-600 to-gray-500',
    description: 'Conversaciones anteriores'
  }
};

// Legal Areas
const LEGAL_AREAS = {
  civil: { name: 'Derecho Civil', icon: <Scale className="w-4 h-4" />, description: 'Compraventas, arriendos, préstamos', keywords: ['comprar', 'vender', 'arrendar', 'préstamo', 'herencia'] },
  laboral: { name: 'Derecho Laboral', icon: <Briefcase className="w-4 h-4" />, description: 'Contratos de trabajo, finiquitos', keywords: ['trabajo', 'empleado', 'despido', 'finiquito', 'sueldo'] },
  familia: { name: 'Derecho de Familia', icon: <Heart className="w-4 h-4" />, description: 'Matrimonio, divorcio, pensión', keywords: ['matrimonio', 'divorcio', 'pensión', 'custodia', 'alimentos'] },
  comercial: { name: 'Derecho Comercial', icon: <Building2 className="w-4 h-4" />, description: 'Sociedades, pagarés', keywords: ['empresa', 'sociedad', 'pagaré', 'comercial', 'negocio'] },
  inmobiliario: { name: 'Derecho Inmobiliario', icon: <Home className="w-4 h-4" />, description: 'Propiedades, hipotecas', keywords: ['propiedad', 'casa', 'departamento', 'hipoteca', 'inmueble'] },
  vehicular: { name: 'Vehículos', icon: <Car className="w-4 h-4" />, description: 'Compraventa de vehículos', keywords: ['auto', 'vehículo', 'carro', 'moto', 'traspaso'] }
};

// Writing Suggestions
const WRITING_SUGGESTIONS = {
  compraventa: {
    title: 'Contrato de Compraventa',
    clauses: [
      { type: 'obligatoria', text: 'Identificación completa de las partes (RUT, domicilio, estado civil)' },
      { type: 'obligatoria', text: 'Descripción detallada del bien objeto de la venta' },
      { type: 'obligatoria', text: 'Precio y forma de pago claramente estipulados' },
      { type: 'recomendada', text: 'Cláusula de garantía de evicción' },
      { type: 'recomendada', text: 'Cláusula de vicios ocultos con plazo de reclamo' }
    ],
    exceptions: [
      'Art. 1810 CC: No pueden venderse las cosas que están fuera del comercio',
      'Art. 1796 CC: Prohibición de venta entre cónyuges no separados judicialmente',
      'Art. 1797 CC: Prohibición de venta entre padre/hijo de familia'
    ],
    tips: ['Verificar titularidad del bien', 'Incluir cláusula penal', 'Especificar gastos notariales']
  },
  arriendo: {
    title: 'Contrato de Arriendo',
    clauses: [
      { type: 'obligatoria', text: 'Identificación del inmueble con dirección exacta' },
      { type: 'obligatoria', text: 'Monto de la renta y fecha de pago' },
      { type: 'obligatoria', text: 'Duración del contrato (plazo fijo o indefinido)' },
      { type: 'recomendada', text: 'Cláusula de reajuste según IPC' },
      { type: 'recomendada', text: 'Inventario detallado del inmueble' }
    ],
    exceptions: [
      'Art. 1943 CC: El arrendador debe entregar la cosa en estado de servir',
      'Art. 1973 CC: Desahucio judicial para terminar arrendamiento',
      'Ley 21.461: Nuevo procedimiento de desalojo'
    ],
    tips: ['Exigir mes de garantía', 'Incluir prohibición de subarriendo', 'Fotografiar estado inicial']
  },
  laboral: {
    title: 'Contrato de Trabajo',
    clauses: [
      { type: 'obligatoria', text: 'Lugar y fecha del contrato' },
      { type: 'obligatoria', text: 'Individualización de las partes' },
      { type: 'obligatoria', text: 'Naturaleza de los servicios y lugar de prestación' },
      { type: 'obligatoria', text: 'Monto, forma y período de pago de la remuneración' },
      { type: 'recomendada', text: 'Cláusula de confidencialidad' }
    ],
    exceptions: [
      'Art. 159 CT: Causales de terminación del contrato',
      'Art. 161 CT: Necesidades de la empresa',
      'Art. 163 CT: Indemnización por años de servicio'
    ],
    tips: ['Escriturar dentro de 15 días', 'Actualizar ante modificaciones', 'Guardar copia firmada']
  },
  sociedad: {
    title: 'Constitución de Sociedad',
    clauses: [
      { type: 'obligatoria', text: 'Nombre, nacionalidad, domicilio y RUT de socios' },
      { type: 'obligatoria', text: 'Razón social y domicilio de la sociedad' },
      { type: 'obligatoria', text: 'Capital social y aportes de cada socio' },
      { type: 'obligatoria', text: 'Objeto social específico y determinado' },
      { type: 'recomendada', text: 'Cláusula de resolución de conflictos (arbitraje)' }
    ],
    exceptions: [
      'Art. 2053 CC: Sociedad nula si no tiene fin lícito',
      'Art. 2054 CC: Nulidad si no hay aporte de todos los socios',
      'Art. 424 Código Comercio: Publicación en Diario Oficial'
    ],
    tips: ['Considerar tipo societario (Ltda, SpA, SA)', 'Definir distribución de utilidades', 'Establecer entrada/salida de socios']
  }
};

// Risk Patterns
const RISK_PATTERNS = {
  high: [
    { pattern: /sin garantía/i, risk: 'Falta de garantía', solution: 'Agregar cláusula de garantía real o personal' },
    { pattern: /plazo indefinido/i, risk: 'Plazo indefinido', solution: 'Considerar establecer plazo determinado' },
    { pattern: /verbal/i, risk: 'Acuerdo verbal', solution: 'Escriturar siempre para tener prueba documental' }
  ],
  medium: [
    { pattern: /renovación automática/i, risk: 'Renovación automática', solution: 'Agregar cláusula de término anticipado' },
    { pattern: /sin límite/i, risk: 'Sin límites establecidos', solution: 'Definir límites claros de responsabilidad' }
  ],
  protective: [
    { pattern: /cláusula penal/i, protection: 'Protección por incumplimiento' },
    { pattern: /arbitraje/i, protection: 'Resolución alternativa de conflictos' },
    { pattern: /notaría/i, protection: 'Documento con fecha cierta' }
  ]
};

// Icon mapping for judicial system
const iconMap = {
  Scale, Building2, Gavel, Shield, Users, Heart, Briefcase,
  Banknote, Car, BookOpen, Leaf, Receipt, FileCheck, MapPin, TrendingUp
};

// ================ UNIFIED ASSISTANT COMPONENT ================

const UnifiedAIAssistant = ({ readingMode = false }) => {
  const { user, token } = useAuth();
  // Core State
  const [isOpen, setIsOpen] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [currentMode, setCurrentMode] = useState(user ? 'copilot' : 'general');
  
  // General Assistant State
  const [messages, setMessages] = useState([{
    id: 1, type: 'bot',
    text: '¡Hola! Soy tu asesor legal de NotaryOS. ¿En qué puedo ayudarte?',
    time: new Date(), showQuickActions: true
  }]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [detectedArea, setDetectedArea] = useState(null);
  
  // Drafting State
  const [draftingTab, setDraftingTab] = useState('suggest');
  const [selectedContractType, setSelectedContractType] = useState('compraventa');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Judicial State
  const [judicialTab, setJudicialTab] = useState('explorer');
  const [selectedJudicialCategory, setSelectedJudicialCategory] = useState('ordinarios');
  const [selectedTribunal, setSelectedTribunal] = useState(null);
  const [judicialSearchQuery, setJudicialSearchQuery] = useState('');
  const [finderResult, setFinderResult] = useState(null);
  
  // Strategy State
  const [strategyResult, setStrategyResult] = useState(null);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [selectedLegalArea, setSelectedLegalArea] = useState('general');
  
  // History State
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Common State
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => { setCurrentMode(user ? 'copilot' : 'general'); setIsOpen(false); }, [user?.id]);
  useEffect(() => {
    const close = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  // Auto-scroll and focus
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen, currentMode]);

  // Mode configuration
  const modeConfig = ASSISTANT_MODES[currentMode];

  // ================ GENERAL ASSISTANT LOGIC ================
  
  const detectLegalArea = (text) => {
    const lowerText = text.toLowerCase();
    for (const [key, area] of Object.entries(LEGAL_AREAS)) {
      for (const keyword of area.keywords) {
        if (lowerText.includes(keyword)) return { key, ...area };
      }
    }
    return null;
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;
    
    const userMessage = { id: Date.now(), type: 'user', text: inputText, time: new Date() };
    setMessages(prev => [...prev, userMessage]);
    const userText = inputText;
    setInputText('');
    setIsTyping(true);

    const area = detectLegalArea(userText);
    if (area) setDetectedArea(area);

    try {
      if (token) {
        const response = await api.post('/chat', { message: userText });
        setMessages(prev => [...prev, {
          id: Date.now(), type: 'bot', text: response.data.response, time: new Date(),
          actions: [
            { text: 'Ver plantillas', action: () => navigate('/templates') },
            { text: 'Crear contrato', action: () => navigate('/discovery') }
          ]
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now(), type: 'bot', time: new Date(),
          text: area 
            ? `Tu caso corresponde al área de **${area.name}**. Te recomiendo usar nuestras plantillas legales.`
            : 'Para una mejor asesoría, cuéntame qué tipo de documento necesitas o qué transacción quieres realizar.',
          showLegalAreas: !area
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now(), type: 'bot', time: new Date(),
        text: 'Hubo un problema. ¿Podrías reformular tu pregunta?'
      }]);
    }
    
    setIsTyping(false);
  };

  const handleAreaSelect = (area) => {
    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text: `Me interesa ${area.name}`, time: new Date() }]);
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(), type: 'bot', time: new Date(),
        text: `En **${area.name}** tenemos múltiples plantillas. ¿Quieres verlas o prefieres que analice tu caso?`,
        actions: [
          { text: 'Ver plantillas', action: () => navigate('/templates') },
          { text: 'Analizar mi caso', action: () => navigate('/recommend') }
        ]
      }]);
      setIsTyping(false);
    }, 600);
  };

  // ================ DRAFTING LOGIC ================

  const analyzeContract = async () => {
    if (!inputText.trim()) { toast.error('Ingresa el texto del contrato'); return; }
    setIsAnalyzing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    const risks = [];
    const protections = [];
    let riskScore = 20;

    RISK_PATTERNS.high.forEach(({ pattern, risk, solution }) => {
      if (pattern.test(inputText)) { risks.push({ level: 'alto', risk, solution }); riskScore += 25; }
    });
    RISK_PATTERNS.medium.forEach(({ pattern, risk, solution }) => {
      if (pattern.test(inputText)) { risks.push({ level: 'medio', risk, solution }); riskScore += 15; }
    });
    RISK_PATTERNS.protective.forEach(({ pattern, protection }) => {
      if (pattern.test(inputText)) { protections.push(protection); riskScore -= 10; }
    });

    riskScore = Math.max(0, Math.min(100, riskScore));
    setAnalysisResult({ riskScore, risks, protections, recommendations: ['Revisar cláusulas de riesgo', 'Agregar más protecciones', 'Verificar normativa vigente'] });
    setIsAnalyzing(false);
  };

  // ================ JUDICIAL LOGIC ================

  const findTribunal = () => {
    if (!judicialSearchQuery.trim()) { toast.error('Describe tu caso'); return; }
    const query = judicialSearchQuery.toLowerCase();
    let matches = [];

    for (const [keyword, data] of Object.entries(COMPETENCE_MATCHER.keywords)) {
      if (query.includes(keyword)) {
        const category = JUDICIAL_SYSTEM[data.tipo];
        const tribunal = category?.tribunales[data.tribunal];
        if (tribunal) matches.push({ keyword, tribunal, tipo: data.tipo, tribunalId: data.tribunal });
      }
    }

    setFinderResult(matches.length > 0 
      ? { success: true, matches: matches.slice(0, 3), query: judicialSearchQuery }
      : { success: false, message: 'No se encontró tribunal específico. Consulta con un abogado.', query: judicialSearchQuery }
    );
  };

  // ================ STRATEGY LOGIC ================

  const generateLegalStrategy = async () => {
    if (!inputText.trim()) { toast.error('Describe tu situación legal'); return; }
    setIsGeneratingStrategy(true);
    setStrategyResult(null);

    try {
      const response = await api.post('/chat/strategy', {
        situation: inputText,
        legal_area: selectedLegalArea
      });
      setStrategyResult(response.data.strategy);
      toast.success('Estrategia legal generada');
    } catch (error) {
      console.error('Error generating strategy:', error);
      toast.error('Error al generar la estrategia');
      setStrategyResult('No se pudo generar la estrategia legal. Por favor, intenta nuevamente.');
    }
    setIsGeneratingStrategy(false);
  };

  // ================ HISTORY LOGIC ================

  const loadChatHistory = async () => {
    if (!token) return;
    setIsLoadingHistory(true);
    try {
      const response = await api.get('/chat/sessions');
      setChatSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
      toast.error('Error al cargar el historial');
    }
    setIsLoadingHistory(false);
  };

  const loadSessionMessages = async (session) => {
    setSelectedSession(session);
    // Messages are already loaded in the session object
  };

  const clearChatHistory = async () => {
    if (!confirm('¿Estás seguro de que deseas eliminar todo el historial de conversaciones?')) return;
    try {
      await api.delete('/chat/history');
      setChatSessions([]);
      setSelectedSession(null);
      toast.success('Historial eliminado');
    } catch (error) {
      toast.error('Error al eliminar el historial');
    }
  };

  // Load history when switching to history mode
  useEffect(() => {
    if (currentMode === 'history' && token) {
      loadChatHistory();
    }
  }, [currentMode, token]);

  // ================ UTILITIES ================

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copiado');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const switchMode = (newMode) => {
    setCurrentMode(newMode);
    setShowModeSelector(false);
    if (newMode === 'general') setMessages([{ id: Date.now(), type: 'bot', text: '¡Hola! ¿En qué puedo ayudarte?', time: new Date() }]);
    if (newMode === 'strategy') setStrategyResult(null);
    if (newMode === 'history') setSelectedSession(null);
    setInputText('');
    setAnalysisResult(null);
    setFinderResult(null);
  };

  const currentSuggestions = WRITING_SUGGESTIONS[selectedContractType];
  const currentJudicialCategory = JUDICIAL_SYSTEM[selectedJudicialCategory];
  const judicialCategories = [
    { id: 'ordinarios', name: 'Ordinarios', icon: Scale, color: 'blue' },
    { id: 'especiales', name: 'Especiales', icon: Gavel, color: 'green' },
    { id: 'colegiados', name: 'Colegiados', icon: Building2, color: 'purple' }
  ];
  const contractTypes = [
    { id: 'compraventa', label: 'Compraventa', icon: FileText },
    { id: 'arriendo', label: 'Arriendo', icon: BookOpen },
    { id: 'laboral', label: 'Laboral', icon: Gavel },
    { id: 'sociedad', label: 'Sociedad', icon: Scale }
  ];

  // ================ RENDER ================

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Cerrar asistente' : user ? 'Hablar con Fidem' : 'Abrir asesor legal'}
        aria-expanded={isOpen}
        className={`fixed ${readingMode ? 'bottom-4' : 'bottom-24'} right-4 z-50 h-12 ${user ? 'px-4 gap-2' : 'w-14'} bg-gradient-to-r ${modeConfig.color} rounded-full shadow-lg flex items-center justify-center text-white hover:scale-105 transition-transform`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="unified-assistant-btn"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className="relative">
              <modeConfig.icon className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>
        {user && <span className="text-sm font-semibold">Fidem</span>}
      </motion.button>

      {/* Main Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            role="dialog"
            aria-label={currentMode === 'copilot' ? 'Fidem, tu copiloto' : 'Asistente legal'}
            className={`fixed ${readingMode ? 'bottom-20 max-h-[calc(100dvh-100px)]' : 'bottom-40 max-h-[calc(100dvh-180px)]'} right-3 z-50 w-[440px] max-w-[calc(100vw-24px)] bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-y-auto`}
            data-testid="unified-assistant-panel"
          >
            {/* Header with Mode Selector */}
            <div className={`bg-gradient-to-r ${modeConfig.color} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <modeConfig.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{modeConfig.name}</h3>
                    <p className="text-white/80 text-xs">{modeConfig.description}</p>
                  </div>
                </div>
                
                {/* Mode Selector Dropdown */}
                <div className="relative">
                  <button
                    data-testid="assistant-mode-toggle"
                    onClick={() => setShowModeSelector(!showModeSelector)}
                    className="bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 flex items-center space-x-1 text-white text-xs transition-colors"
                  >
                    <span>Cambiar</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showModeSelector ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {showModeSelector && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-10 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-xl min-w-[160px]"
                      >
                        {Object.values(ASSISTANT_MODES).filter(mode => mode.id !== 'copilot' || user).map(mode => (
                          <button
                            key={mode.id}
                            data-testid={`assistant-mode-${mode.id}`}
                            onClick={() => switchMode(mode.id)}
                            className={`w-full flex items-center space-x-2 px-3 py-2 text-left text-sm transition-colors ${
                              currentMode === mode.id ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-700/50'
                            }`}
                          >
                            <mode.icon className="w-4 h-4" />
                            <span>{mode.name}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Content Area - Changes based on mode */}
            <div className={`${currentMode === 'copilot' ? 'p-3' : 'h-[420px] overflow-hidden'} flex flex-col`}>
              {currentMode === 'copilot' && user && <FidemChat compact prefix="copilot" height="h-[24vh] min-h-[130px]" onNavigated={() => setIsOpen(false)} suggest={['Llévame a mis casos', 'Abre Cerebro', '¿Cuántos días me quedan de plan?', '¿Cómo confirmo la corte?']} />}
              
              {/* ============ GENERAL MODE ============ */}
              {currentMode === 'general' && (
                <>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map(msg => (
                      <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex items-start space-x-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.type === 'user' ? 'bg-blue-600' : 'bg-gradient-to-r from-cyan-500 to-blue-500'
                          }`}>
                            {msg.type === 'user' ? <User className="w-3.5 h-3.5 text-white" /> : <Bot className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="space-y-2">
                            <div className={`rounded-2xl px-3 py-2 ${
                              msg.type === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-slate-800 text-slate-200 rounded-bl-md'
                            }`}>
                              {msg.type === 'user' ? <p className="text-sm whitespace-pre-line">{msg.text}</p> : <MarkdownText className="text-sm">{msg.text}</MarkdownText>}
                            </div>
                            {msg.actions && (
                              <div className="flex flex-wrap gap-1.5 ml-1">
                                {msg.actions.map((action, idx) => (
                                  <button key={idx} onClick={action.action}
                                    className="flex items-center space-x-1 px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-xs rounded-full border border-blue-500/30">
                                    <span>{action.text}</span>
                                    <ArrowRight className="w-3 h-3" />
                                  </button>
                                ))}
                              </div>
                            )}
                            {msg.showLegalAreas && (
                              <div className="grid grid-cols-2 gap-1.5 mt-2">
                                {Object.entries(LEGAL_AREAS).slice(0, 6).map(([key, area]) => (
                                  <button key={key} onClick={() => handleAreaSelect(area)}
                                    className="flex items-center space-x-1.5 p-1.5 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 rounded-lg text-left">
                                    <div className="text-cyan-400">{area.icon}</div>
                                    <span className="text-xs text-slate-300">{area.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    {isTyping && (
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                          <Bot className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="bg-slate-800 rounded-2xl rounded-bl-md px-3 py-2">
                          <div className="flex space-x-1">
                            {[0, 150, 300].map(delay => (
                              <span key={delay} className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="p-3 border-t border-slate-700 bg-slate-900">
                    <div className="flex items-center space-x-2">
                      <input ref={inputRef} type="text" value={inputText} onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Describe tu situación legal..."
                        className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                      <button onClick={handleSendMessage} disabled={!inputText.trim() || isTyping}
                        className="w-9 h-9 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded-xl flex items-center justify-center">
                        {isTyping ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ============ DRAFTING MODE ============ */}
              {currentMode === 'drafting' && (
                <>
                  <div className="flex border-b border-slate-700 flex-shrink-0">
                    {[{ id: 'suggest', label: 'Cláusulas', icon: Lightbulb }, { id: 'analyze', label: 'Analizar', icon: AlertTriangle }, { id: 'exceptions', label: 'Excepciones', icon: Scale }].map(tab => (
                      <button key={tab.id} onClick={() => { setDraftingTab(tab.id); setAnalysisResult(null); }}
                        className={`flex-1 flex items-center justify-center space-x-1 py-2 text-xs transition-colors ${
                          draftingTab === tab.id ? 'bg-slate-800 text-white border-b-2 border-purple-500' : 'text-slate-400 hover:text-white'
                        }`}>
                        <tab.icon className="w-3.5 h-3.5" />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {draftingTab === 'suggest' && (
                      <>
                        <div className="grid grid-cols-2 gap-1.5">
                          {contractTypes.map(type => (
                            <button key={type.id} onClick={() => setSelectedContractType(type.id)}
                              className={`flex items-center space-x-1.5 p-2 rounded-lg text-xs transition-colors ${
                                selectedContractType === type.id ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                              }`}>
                              <type.icon className="w-3.5 h-3.5" />
                              <span>{type.label}</span>
                            </button>
                          ))}
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-white mb-2 flex items-center space-x-1">
                            <FileText className="w-3.5 h-3.5 text-purple-400" />
                            <span>Cláusulas Recomendadas</span>
                          </h4>
                          <div className="space-y-1.5">
                            {currentSuggestions?.clauses.map((clause, idx) => (
                              <div key={idx} className="flex items-start space-x-2 p-2 bg-slate-800/50 rounded-lg group">
                                <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                                  clause.type === 'obligatoria' ? 'bg-red-500' : 'bg-yellow-500'
                                }`} />
                                <div className="flex-1">
                                  <p className="text-xs text-slate-300">{clause.text}</p>
                                  <span className={`text-[10px] ${clause.type === 'obligatoria' ? 'text-red-400' : 'text-yellow-400'}`}>
                                    {clause.type}
                                  </span>
                                </div>
                                <button onClick={() => copyToClipboard(clause.text, idx)}
                                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white">
                                  {copiedIndex === idx ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-white mb-1 flex items-center space-x-1">
                            <Star className="w-3.5 h-3.5 text-yellow-400" />
                            <span>Tips</span>
                          </h4>
                          {currentSuggestions?.tips.map((tip, idx) => (
                            <div key={idx} className="flex items-start space-x-1.5 text-xs text-slate-400">
                              <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {draftingTab === 'analyze' && (
                      <>
                        <textarea ref={inputRef} value={inputText} onChange={(e) => setInputText(e.target.value)}
                          placeholder="Pega aquí el texto del contrato..."
                          className="w-full h-24 bg-slate-800 border border-slate-700 rounded-lg p-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-purple-500" />
                        <button onClick={analyzeContract} disabled={isAnalyzing || !inputText.trim()}
                          className="w-full py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 text-white rounded-lg text-xs font-medium flex items-center justify-center space-x-1">
                          {isAnalyzing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Analizando...</span></> : <><Zap className="w-3.5 h-3.5" /><span>Analizar</span></>}
                        </button>
                        {analysisResult && (
                          <div className="space-y-3">
                            <div className="bg-slate-800/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-slate-400">Nivel de Riesgo</span>
                                <span className={`text-xl font-bold ${analysisResult.riskScore > 60 ? 'text-red-500' : analysisResult.riskScore > 30 ? 'text-yellow-500' : 'text-green-500'}`}>
                                  {analysisResult.riskScore}%
                                </span>
                              </div>
                              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <motion.div initial={{ width: 0 }} animate={{ width: `${analysisResult.riskScore}%` }}
                                  className={`h-full rounded-full ${analysisResult.riskScore > 60 ? 'bg-red-500' : analysisResult.riskScore > 30 ? 'bg-yellow-500' : 'bg-green-500'}`} />
                              </div>
                            </div>
                            {analysisResult.risks.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium text-white mb-1 flex items-center space-x-1">
                                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                                  <span>Riesgos</span>
                                </h4>
                                {analysisResult.risks.map((risk, idx) => (
                                  <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-1.5">
                                    <p className="text-xs text-red-400 font-medium">{risk.risk}</p>
                                    <p className="text-xs text-slate-400 mt-0.5"><span className="text-green-400">Solución:</span> {risk.solution}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            {analysisResult.protections.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {analysisResult.protections.map((p, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">{p}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {draftingTab === 'exceptions' && (
                      <>
                        <div className="grid grid-cols-2 gap-1.5">
                          {contractTypes.map(type => (
                            <button key={type.id} onClick={() => setSelectedContractType(type.id)}
                              className={`flex items-center space-x-1.5 p-2 rounded-lg text-xs transition-colors ${
                                selectedContractType === type.id ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                              }`}>
                              <type.icon className="w-3.5 h-3.5" />
                              <span>{type.label}</span>
                            </button>
                          ))}
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-white mb-2 flex items-center space-x-1">
                            <Scale className="w-3.5 h-3.5 text-blue-400" />
                            <span>Excepciones - {currentSuggestions?.title}</span>
                          </h4>
                          {currentSuggestions?.exceptions.map((exc, idx) => (
                            <motion.div key={idx} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                              className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 mb-1.5 group">
                              <div className="flex items-start justify-between">
                                <p className="text-xs text-slate-300">{exc}</p>
                                <button onClick={() => copyToClipboard(exc, `exc-${idx}`)}
                                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white ml-1">
                                  {copiedIndex === `exc-${idx}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-2">
                          <h4 className="text-xs font-medium text-yellow-400 mb-1 flex items-center space-x-1">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Excepciones Generales</span>
                          </h4>
                          <ul className="text-xs text-slate-400 space-y-1">
                            <li><strong>Art. 1545 CC:</strong> Contrato es ley para los contratantes</li>
                            <li><strong>Art. 1546 CC:</strong> Ejecución de buena fe</li>
                            <li><strong>Art. 1560 CC:</strong> Intención prevalece sobre lo literal</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* ============ JUDICIAL MODE ============ */}
              {currentMode === 'judicial' && (
                <>
                  <div className="flex border-b border-slate-700 flex-shrink-0">
                    {[{ id: 'explorer', label: 'Explorar', icon: Building2 }, { id: 'finder', label: 'Buscador', icon: Search }, { id: 'recursos', label: 'Recursos', icon: FileText }, { id: 'plazos', label: 'Plazos', icon: Clock }].map(tab => (
                      <button key={tab.id} onClick={() => setJudicialTab(tab.id)}
                        className={`flex-1 flex items-center justify-center space-x-1 py-2 text-xs transition-colors ${
                          judicialTab === tab.id ? 'bg-slate-800 text-white border-b-2 border-amber-500' : 'text-slate-400 hover:text-white'
                        }`}>
                        <tab.icon className="w-3.5 h-3.5" />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {judicialTab === 'explorer' && (
                      <>
                        <div className="flex gap-1.5">
                          {judicialCategories.map(cat => (
                            <button key={cat.id} onClick={() => { setSelectedJudicialCategory(cat.id); setSelectedTribunal(null); }}
                              className={`flex-1 flex items-center justify-center space-x-1 py-1.5 rounded-lg text-xs transition-colors ${
                                selectedJudicialCategory === cat.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                              }`}>
                              <cat.icon className="w-3 h-3" />
                              <span>{cat.name}</span>
                            </button>
                          ))}
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-2">
                          <h4 className="text-xs font-medium text-white">{currentJudicialCategory?.title}</h4>
                          <p className="text-xs text-slate-400">{currentJudicialCategory?.description}</p>
                        </div>
                        <div className="space-y-1.5">
                          {Object.entries(currentJudicialCategory?.tribunales || {}).map(([id, tribunal]) => {
                            const IconComponent = iconMap[tribunal.icon] || Scale;
                            const isSelected = selectedTribunal?.id === id;
                            return (
                              <div key={id}>
                                <button onClick={() => setSelectedTribunal(isSelected ? null : { ...tribunal, id })}
                                  className={`w-full text-left p-2 rounded-lg transition-colors ${
                                    isSelected ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-slate-800/50 hover:bg-slate-800'
                                  }`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <IconComponent className={`w-3.5 h-3.5 ${isSelected ? 'text-amber-400' : 'text-slate-400'}`} />
                                      <span className={`text-xs ${isSelected ? 'text-white font-medium' : 'text-slate-300'}`}>{tribunal.nombre}</span>
                                    </div>
                                    <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                                  </div>
                                </button>
                                <AnimatePresence>
                                  {isSelected && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                      className="overflow-hidden">
                                      <div className="p-2 bg-slate-800/30 rounded-b-lg space-y-2 border-x border-b border-amber-500/20">
                                        {tribunal.competencia && (
                                          <div>
                                            <h5 className="text-xs font-medium text-amber-400 mb-1">Competencia</h5>
                                            <ul className="space-y-0.5">
                                              {tribunal.competencia.slice(0, 4).map((comp, idx) => (
                                                <li key={idx} className="text-xs text-slate-400 flex items-start space-x-1">
                                                  <span className="text-amber-500">•</span>
                                                  <span>{comp}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {tribunal.procedimientos && (
                                          <div>
                                            <h5 className="text-xs font-medium text-green-400 mb-1">Procedimientos</h5>
                                            {tribunal.procedimientos.map((proc, idx) => (
                                              <div key={idx} className="flex items-center justify-between text-xs mb-0.5">
                                                <span className="text-slate-300">{proc.nombre}</span>
                                                <span className="text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded text-[10px]">{proc.plazo}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {judicialTab === 'finder' && (
                      <>
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                          <div className="flex items-start space-x-2">
                            <Sparkles className="w-3.5 h-3.5 text-amber-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-white font-medium">Buscador de Competencia</p>
                              <p className="text-xs text-slate-400">Describe tu caso para encontrar el tribunal</p>
                            </div>
                          </div>
                        </div>
                        <textarea value={judicialSearchQuery} onChange={(e) => setJudicialSearchQuery(e.target.value)}
                          placeholder="Ej: Demanda por despido injustificado..."
                          className="w-full h-20 bg-slate-800 border border-slate-700 rounded-lg p-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-amber-500" />
                        <button onClick={findTribunal}
                          className="w-full py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium flex items-center justify-center space-x-1">
                          <Search className="w-3.5 h-3.5" />
                          <span>Buscar Tribunal</span>
                        </button>
                        {finderResult && (
                          <div className="space-y-2">
                            {finderResult.success ? (
                              <>
                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                                  <p className="text-xs text-green-400 font-medium flex items-center space-x-1">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    <span>Tribunales encontrados</span>
                                  </p>
                                </div>
                                {finderResult.matches.map((match, idx) => {
                                  const IconComponent = iconMap[match.tribunal.icon] || Scale;
                                  return (
                                    <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-lg p-2">
                                      <div className="flex items-center space-x-1.5 mb-1">
                                        <IconComponent className="w-3.5 h-3.5 text-amber-400" />
                                        <span className="text-xs text-white font-medium">{match.tribunal.nombre}</span>
                                      </div>
                                      <p className="text-xs text-slate-400">Detectado: <span className="text-amber-400">"{match.keyword}"</span></p>
                                    </div>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                                <p className="text-xs text-yellow-300">{finderResult.message}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {['despido', 'divorcio', 'pensión', 'accidente', 'impuestos'].map(ex => (
                            <button key={ex} onClick={() => setJudicialSearchQuery(ex)}
                              className="text-xs bg-slate-800 text-slate-400 hover:text-white px-2 py-0.5 rounded">{ex}</button>
                          ))}
                        </div>
                      </>
                    )}
                    {judicialTab === 'recursos' && (
                      Object.entries(RECURSOS_PROCESALES).map(([tipo, recursos]) => (
                        <div key={tipo}>
                          <h4 className="text-xs font-medium text-white mb-1.5 capitalize flex items-center space-x-1">
                            <FileText className="w-3.5 h-3.5 text-amber-400" />
                            <span>Recursos {tipo}</span>
                          </h4>
                          <div className="space-y-1.5">
                            {recursos.map((recurso, idx) => (
                              <div key={idx} className="bg-slate-800/50 rounded-lg p-2 group">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="text-xs text-white font-medium">{recurso.nombre}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">{recurso.descripcion}</p>
                                  </div>
                                  <button onClick={() => copyToClipboard(`${recurso.nombre}: ${recurso.plazo}`, `rec-${idx}`)}
                                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-white">
                                    {copiedIndex === `rec-${idx}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                                <div className="flex gap-1.5 mt-1.5">
                                  <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded flex items-center space-x-0.5">
                                    <Clock className="w-3 h-3" />
                                    <span>{recurso.plazo}</span>
                                  </span>
                                  <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">{recurso.tribunal}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                    {judicialTab === 'plazos' && (
                      <>
                        {Object.entries(PLAZOS_JUDICIALES).map(([materia, plazos]) => (
                          <div key={materia}>
                            <h4 className="text-xs font-medium text-white mb-1.5 capitalize flex items-center space-x-1">
                              <Clock className="w-3.5 h-3.5 text-amber-400" />
                              <span>Plazos {materia}</span>
                            </h4>
                            <div className="bg-slate-800/50 rounded-lg overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-slate-800">
                                    <th className="text-left p-1.5 text-slate-400">Concepto</th>
                                    <th className="text-right p-1.5 text-slate-400">Plazo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {plazos.map((item, idx) => (
                                    <tr key={idx} className="border-t border-slate-700">
                                      <td className="p-1.5 text-slate-300">{item.concepto}</td>
                                      <td className="p-1.5 text-right text-amber-400 font-medium">{item.plazo}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                          <p className="text-xs text-yellow-300 flex items-start space-x-1">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>Los plazos de días son hábiles salvo indicación contraria.</span>
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* ============ STRATEGY MODE ============ */}
              {currentMode === 'strategy' && (
                <>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <TrendingUp className="w-4 h-4 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-white font-medium">Generador de Estrategia Legal</p>
                          <p className="text-xs text-slate-400">Describe tu situación y recibirás una estrategia basada en ley chilena y jurisprudencia de tribunales</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Área Legal</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[
                          { id: 'civil', name: 'Civil' },
                          { id: 'laboral', name: 'Laboral' },
                          { id: 'familia', name: 'Familia' },
                          { id: 'comercial', name: 'Comercial' },
                          { id: 'penal', name: 'Penal' },
                          { id: 'general', name: 'General' }
                        ].map(area => (
                          <button
                            key={area.id}
                            onClick={() => setSelectedLegalArea(area.id)}
                            className={`p-1.5 rounded text-xs transition-colors ${
                              selectedLegalArea === area.id
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                            }`}
                          >
                            {area.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Describe tu situación</label>
                      <textarea
                        ref={inputRef}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Ej: Fui despedido sin causa justificada después de 5 años trabajando. Mi empleador no me pagó la indemnización completa..."
                        className="w-full h-28 bg-slate-800 border border-slate-700 rounded-lg p-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-emerald-500"
                      />
                    </div>

                    <button
                      onClick={generateLegalStrategy}
                      disabled={isGeneratingStrategy || !inputText.trim()}
                      className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded-lg text-sm font-medium flex items-center justify-center space-x-2"
                    >
                      {isGeneratingStrategy ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Generando estrategia...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          <span>Generar Estrategia Legal</span>
                        </>
                      )}
                    </button>

                    {strategyResult && (
                      <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-medium text-emerald-400 flex items-center space-x-1">
                            <Scale className="w-3.5 h-3.5" />
                            <span>Estrategia Legal</span>
                          </h4>
                          <button
                            onClick={() => copyToClipboard(strategyResult, 'strategy')}
                            className="text-slate-400 hover:text-white"
                          >
                            {copiedIndex === 'strategy' ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <div className="prose prose-invert prose-sm max-w-none">
                          <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed">
                            {strategyResult}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                      <p className="text-xs text-yellow-300 flex items-start space-x-1">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Esta es orientación general basada en ley chilena. Consulte con un abogado para asesoría específica.</span>
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* ============ HISTORY MODE ============ */}
              {currentMode === 'history' && (
                <>
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-white flex items-center space-x-1">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>Historial de Conversaciones</span>
                      </h4>
                      {chatSessions.length > 0 && (
                        <button
                          onClick={clearChatHistory}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Eliminar todo
                        </button>
                      )}
                    </div>

                    {isLoadingHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                      </div>
                    ) : !token ? (
                      <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                        <p className="text-xs text-slate-400 mb-2">Inicia sesión para ver tu historial</p>
                        <button
                          onClick={() => navigate('/auth')}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Iniciar sesión
                        </button>
                      </div>
                    ) : chatSessions.length === 0 ? (
                      <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                        <p className="text-xs text-slate-400">No hay conversaciones guardadas</p>
                        <p className="text-xs text-slate-500 mt-1">Tus consultas aparecerán aquí</p>
                      </div>
                    ) : selectedSession ? (
                      <>
                        <button
                          onClick={() => setSelectedSession(null)}
                          className="flex items-center space-x-1 text-xs text-blue-400 hover:text-blue-300"
                        >
                          <ChevronRight className="w-3 h-3 rotate-180" />
                          <span>Volver al historial</span>
                        </button>
                        <div className="bg-slate-800/30 rounded-lg p-2">
                          <p className="text-xs text-slate-400 mb-2">{selectedSession.date}</p>
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {selectedSession.messages?.map((msg, idx) => (
                              <div
                                key={idx}
                                className={`p-2 rounded-lg text-xs ${
                                  msg.role === 'user'
                                    ? 'bg-blue-500/20 text-blue-200 ml-4'
                                    : 'bg-slate-700/50 text-slate-300 mr-4'
                                }`}
                              >
                                <p className="text-[10px] text-slate-500 mb-1">
                                  {msg.role === 'user' ? 'Tú' : 'Asesor'}
                                </p>
                                {msg.role === 'user' ? <p className="whitespace-pre-line">{msg.content}</p> : <MarkdownText>{msg.content}</MarkdownText>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        {chatSessions.map((session, idx) => (
                          <button
                            key={idx}
                            onClick={() => loadSessionMessages(session)}
                            className="w-full text-left p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors group"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-slate-400">{session.date}</span>
                              <ChevronRight className="w-3 h-3 text-slate-500 group-hover:text-white" />
                            </div>
                            <p className="text-xs text-slate-300 truncate">
                              {session.preview || 'Conversación'}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-1">
                              {session.messages?.length || 0} mensajes
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-2 border-t border-slate-700 bg-slate-900">
              <p className="text-center text-xs text-slate-500">
                NotaryOS - Derecho Chileno
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default UnifiedAIAssistant;
